import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, ChevronRight, FileText, Target } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  buildPhaseGoalRows,
  CORE_IDEA,
  getProgressionPhaseGoals,
  JOURNEY_ENDPOINTS,
  PHASE_NARRATIVES,
  PROGRESSION_DIAGRAM_PDF,
  PROGRESSION_GLOSSARY,
  TROUBLESHOOTING_INTRO,
  TROUBLESHOOTING_STEPS,
  UNDERLYING_PRINCIPLE,
  type PhaseNarrative,
  type ProgressionPhaseCode,
} from '@/lib/progression-model';

const sectionLabelClass =
  'text-xs font-bold uppercase tracking-wide text-muted';

const sectionPaddingClass = 'p-4 sm:p-6';

/**
 * Nested inset surface — matches open-design badge-muted / phase-badge tint.
 * Defined in globals.css as .nested-surface (auditable by verify-theme-coverage).
 */
const nestedSurfaceClass = 'nested-surface';

/**
 * Table-style zebra row — matches open-design tbody hover stripe.
 * Defined in globals.css as .striped-row (auditable by verify-theme-coverage).
 */
const stripedRowClass = 'striped-row';

const panelClass =
  'overflow-hidden rounded-lg border border-line bg-surface-solid text-fg shadow-sm';

/**
 * Sample drill pill — open-design badge-muted (neutral on any surface).
 * Uses .nested-surface from globals.css (auditable by verify-theme-coverage).
 */
const drillPillClass =
  'nested-surface inline-flex rounded-full border border-line px-2.5 py-1 text-xs font-semibold text-fg';

/** Clinical warn / clear banners — open-design clinical-banner pattern */
const warnBannerClass =
  'rounded-md border border-status-warn border-l-[3px] bg-status-warn-surface px-4 py-3 text-sm leading-relaxed text-status-warn';

const clearBannerClass =
  'rounded-md border border-status-clear border-l-[3px] bg-status-clear-surface px-4 py-3';

export function ProgressionModelView() {
  const [activePhase, setActivePhase] = useState<ProgressionPhaseCode>('P1');
  const narrative = PHASE_NARRATIVES.find((p) => p.code === activePhase) ?? PHASE_NARRATIVES[0];
  const phaseIndex = PHASE_NARRATIVES.findIndex((p) => p.code === activePhase);

  return (
    <div className="mx-auto w-full min-w-0 max-w-6xl pb-10">
      <header className="space-y-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0 flex-1 space-y-2">
            <p className={cn(sectionLabelClass, 'normal-case tracking-wider')}>
              Biomechanics 2.0 · Reference
            </p>
            <h2 className="text-2xl font-bold tracking-tight text-fg sm:text-3xl">
              From dysfunction to optimal movement
            </h2>
            <p className="max-w-3xl text-sm leading-relaxed text-muted sm:text-base">{CORE_IDEA}</p>
          </div>
          <div className="flex shrink-0 flex-col gap-2 sm:flex-row lg:flex-col xl:flex-row">
            <a
              href={PROGRESSION_DIAGRAM_PDF}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-on-accent shadow-sm transition-colors hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:w-auto"
            >
                <FileText className="h-4 w-4" aria-hidden />
                View diagram (PDF)
            </a>
            <Link
              to="/drills"
              className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-line bg-surface px-4 py-2 text-sm font-semibold text-fg transition-colors hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:w-auto"
            >
                Browse drills
                <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          </div>
        </div>
      </header>

      <div className="mt-8 space-y-4 lg:mt-10">
        <nav className="space-y-3" aria-label="Progression phases">
          <JourneyStrip activePhase={activePhase} />

          <div
            className="-mx-1 flex overflow-x-auto border-b border-line px-1 touch-pan-x"
            role="tablist"
            aria-label="Select phase"
          >
            {PHASE_NARRATIVES.map((phase) => (
              <PhaseTab
                key={phase.code}
                phase={phase}
                isActive={activePhase === phase.code}
                onSelect={() => setActivePhase(phase.code)}
              />
            ))}
          </div>
        </nav>

        <div
          className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(280px,340px)]"
          role="tabpanel"
          aria-label={`Phase ${narrative.index}: ${narrative.title}`}
        >
          <div key={narrative.code} className="min-w-0">
            <PhaseDetail narrative={narrative} isLast={phaseIndex === PHASE_NARRATIVES.length - 1} />
          </div>

          <aside className="min-w-0 space-y-4 lg:sticky lg:top-4 lg:self-start">
            <ReferencePanel title="When progress stalls">
              <p className="text-sm leading-relaxed text-fg sm:text-[0.9375rem]">
                {TROUBLESHOOTING_INTRO}
              </p>
              <ol className="mt-5 space-y-4">
                {TROUBLESHOOTING_STEPS.map((step) => (
                  <li
                    key={step.id}
                    className={cn('rounded-md border border-line p-3 sm:p-4', nestedSurfaceClass)}
                  >
                    <p className="text-sm font-semibold text-fg">
                      {step.order}. {step.title}
                    </p>
                    <p className="mt-1 text-sm text-muted">{step.prompt}</p>
                    <ul className="mt-2.5 space-y-1.5">
                      {step.checks.map((check) => (
                        <li key={check} className="flex items-start gap-2 text-sm text-fg">
                          <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
                          {check}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-2.5 text-sm text-success-text">{step.resolution}</p>
                  </li>
                ))}
              </ol>
            </ReferencePanel>

            <ReferencePanel title="Key terms">
              <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-1">
                {PROGRESSION_GLOSSARY.map((entry) => (
                  <div key={entry.term}>
                    <dt className="text-sm font-semibold text-fg">{entry.term}</dt>
                    <dd className="mt-1 text-sm leading-relaxed text-muted">{entry.definition}</dd>
                  </div>
                ))}
              </dl>
            </ReferencePanel>

            <div className={cn(panelClass, 'p-4 shadow-none')}>
              <p className={sectionLabelClass}>Underlying principle</p>
              <p className="mt-2 text-sm leading-relaxed text-fg sm:text-[0.9375rem]">
                {UNDERLYING_PRINCIPLE}
              </p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function JourneyStrip({ activePhase }: { activePhase: ProgressionPhaseCode }) {
  return (
    <div
      className="flex items-center gap-1.5 overflow-x-auto pb-1 text-xs font-semibold touch-pan-x sm:gap-2 sm:text-sm"
      aria-label="Clinical progression journey"
    >
      <span className="shrink-0 text-muted">{JOURNEY_ENDPOINTS.start.label}</span>
      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted" aria-hidden />

      {PHASE_NARRATIVES.map((phase, index) => (
        <span key={phase.code} className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          <span
            className={cn(
              'whitespace-nowrap',
              activePhase === phase.code ? 'text-primary' : 'text-muted',
            )}
          >
            Phase {phase.index}
          </span>
          {index < PHASE_NARRATIVES.length - 1 && (
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted" aria-hidden />
          )}
        </span>
      ))}

      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted" aria-hidden />
      <span className="shrink-0 text-muted">{JOURNEY_ENDPOINTS.end.label}</span>
    </div>
  );
}

function PhaseTab({
  phase,
  isActive,
  onSelect,
}: {
  phase: PhaseNarrative;
  isActive: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      onClick={onSelect}
      className={cn(
        'flex min-h-11 min-w-[7.5rem] shrink-0 items-center gap-2 border-b-2 px-3 py-2 text-left transition-colors sm:min-w-[9rem] sm:px-4',
        isActive
          ? 'border-primary text-primary'
          : 'border-transparent text-muted hover:border-line hover:text-fg',
      )}
    >
      <span
        className={cn(
          'flex h-7 w-7 shrink-0 items-center justify-center rounded-md border text-xs font-bold tabular-nums',
          isActive
            ? 'border-primary/30 bg-primary/10 text-primary'
            : cn('border-line text-muted', nestedSurfaceClass),
        )}
      >
        {phase.index}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-sm font-semibold">Phase {phase.index}</span>
        <span className="block truncate text-xs font-normal text-muted">{phase.title}</span>
      </span>
    </button>
  );
}

function ReferencePanel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <details className={cn('group', panelClass, 'shadow-none')}>
      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-4 py-3 font-semibold text-fg [&::-webkit-details-marker]:hidden">
        <span>{title}</span>
        <span className="text-xs font-normal text-muted group-open:hidden">Expand</span>
        <span className="hidden text-xs font-normal text-muted group-open:inline">Collapse</span>
      </summary>
      <div className="border-t border-line px-4 py-4">{children}</div>
    </details>
  );
}

function PhaseDetail({ narrative, isLast }: { narrative: PhaseNarrative; isLast: boolean }) {
  const goalRows = buildPhaseGoalRows(narrative.code, getProgressionPhaseGoals());

  return (
    <article className={panelClass}>
      <header className={cn('space-y-2 border-b border-line', sectionPaddingClass)}>
        <p className={sectionLabelClass}>
          Phase {narrative.index} · {narrative.title}
        </p>
        <h3 className="text-xl font-bold text-fg sm:text-2xl">{narrative.subtitle}</h3>
        <p className="text-sm leading-relaxed text-muted sm:text-[0.9375rem]">{narrative.summary}</p>
      </header>

      <div className="divide-y divide-line">
        {narrative.patternNote && (
          <section className={sectionPaddingClass} aria-label="Pattern-specific guidance">
            <h4 className={cn(sectionLabelClass, 'mb-3')}>Pattern focus</h4>
            <div className="grid min-w-0 gap-3 sm:grid-cols-2">
              <PatternCallout label="APT" text={narrative.patternNote.apt} />
              <PatternCallout label="Swayback" text={narrative.patternNote.sb} />
            </div>
          </section>
        )}

        <section className={cn(sectionPaddingClass, 'space-y-2')}>
          <h4 className={sectionLabelClass}>Focus</h4>
          <p className="text-sm leading-relaxed text-fg sm:text-[0.9375rem]">{narrative.focus}</p>
        </section>

        <section className={cn(sectionPaddingClass, 'space-y-3')}>
          <div className="flex items-center gap-2">
            <Target className="h-4 w-4 shrink-0 text-primary" aria-hidden />
            <h4 className={cn(sectionLabelClass, 'mb-0')}>Assessment gates</h4>
          </div>
          <ul className="overflow-hidden rounded-md border border-line">
            {goalRows.map((row, i) => (
              <li
                key={row.id}
                className={cn(
                  'flex min-h-11 items-center justify-between gap-4 px-3 py-2.5 sm:px-4',
                  i % 2 === 1 && stripedRowClass,
                )}
              >
                <span className="min-w-0 text-sm text-fg sm:text-[0.9375rem]">
                  {row.label}
                  {row.aptOnly && <span className="text-muted"> (APT)</span>}
                </span>
                <span className="shrink-0 text-sm font-semibold tabular-nums text-fg sm:text-[0.9375rem]">
                  {row.goal}
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section className={cn(sectionPaddingClass, 'space-y-2')}>
          <h4 className={sectionLabelClass}>Sample drills</h4>
          <div className="flex flex-wrap gap-2">
            {narrative.sampleDrills.map((d) => (
              <span key={d} className={drillPillClass}>
                {d}
              </span>
            ))}
          </div>
        </section>

        <section className={sectionPaddingClass}>
          <div className={warnBannerClass}>
            <p>
              <span className="font-semibold">Stay here if</span>
              {' — '}
              {narrative.stayHereIf}
            </p>
          </div>
        </section>

        {!isLast && narrative.transition && (
          <section className={sectionPaddingClass}>
            <div className={clearBannerClass}>
              <p className="text-xs font-semibold uppercase tracking-wide text-status-clear">
                Next: {narrative.transition.name}
              </p>
              <p className="mt-2 text-sm leading-relaxed text-status-clear sm:text-[0.9375rem]">
                {narrative.transition.description}
              </p>
              <ol className="mt-3 list-decimal space-y-1.5 pl-4 text-sm text-status-clear sm:text-[0.9375rem]">
                {narrative.transition.steps.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ol>
            </div>
          </section>
        )}
      </div>
    </article>
  );
}

function PatternCallout({ label, text }: { label: string; text: string }) {
  return (
    <div
      className={cn(
        'min-w-0 rounded-md border border-line border-l-[3px] border-l-primary p-4',
        nestedSurfaceClass,
      )}
    >
      <p className="text-[0.6875rem] font-bold uppercase tracking-wider text-primary">{label}</p>
      <p className="mt-1.5 text-sm leading-relaxed text-fg sm:text-[0.9375rem]">{text}</p>
    </div>
  );
}
